import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {
  AlertIcon,
  ArrowBothIcon,
  ColumnsIcon,
  ListUnorderedIcon,
  NoteIcon,
  ProjectIcon,
  RowsIcon,
} from '@primer/octicons-react'
import {AriaAlert, Label, Spinner} from '@primer/react'
import {useMemo, useRef, useState} from 'react'

import type {ViewConfiguration} from '../../contexts/views/view-configuration'
import type {Layout} from '../../utils/saved-view-url-params'
import {getFieldsForCapability} from './get-fields-for-capability'
import {Capability, type FieldDefinition, type FieldsData} from './fields'
import type {FieldOption} from '../view-options/field-option'
import {ViewOptionsMenu} from '../view-options/ViewOptionsMenu'
import {getFieldIcon} from './field-icons'
import styles from './SavedViewOptionsMenu.module.css'

const LAYOUT_OPTIONS = [
  {value: 'list', label: 'List', icon: ListUnorderedIcon},
  {value: 'board', label: 'Board', icon: ProjectIcon},
] satisfies ReadonlyArray<{value: Layout; label: string; icon: typeof ListUnorderedIcon}>

function toFieldOption({identifier, label, icon}: FieldDefinition): FieldOption {
  return {identifier, label, icon: getFieldIcon(icon)}
}

/** Sort's leading visual, matching memex: a two-way arrow rotated to point up/down. */
function SortByIcon({size}: {size?: number}) {
  return <ArrowBothIcon size={size} className={styles.sortIcon} />
}

type ViewConfigurationDirtyState = {[Key in keyof ViewConfiguration]: boolean}

interface SavedViewOptionsMenuProps {
  fields: FieldsData | undefined
  /** True while the field definitions are being fetched asynchronously. */
  isPending?: boolean
  /** True when the async fetch for the field definitions failed. */
  isError?: boolean
  configuration: ViewConfiguration
  showDirtyIndicator?: boolean
  /** Applied settings that differ from the saved baseline, not the dialog draft. */
  configurationDirty?: ViewConfigurationDirtyState
  /** Prevents configuration changes while the current configuration is being saved. */
  disabled?: boolean
  /** Receives changed settings together on Apply, including the full visible-field selection when changed. */
  onApply: (changes: Partial<ViewConfiguration>) => void
  /** If `true`, each change goes to `onApply` as it is made, and the menu has no Apply or Cancel buttons. */
  applyImmediately?: boolean
  /**
   * Handler that is called with the changes a layout, column, or swimlane option would make,
   * once a pick of it looks likely. Returns whether it sent a request. At most
   * `MAX_INTENT_REQUESTS` calls that send one are made each time the menu opens.
   */
  onIntent?: (changes: Partial<ViewConfiguration>) => boolean
}

/** Bounds the speculative requests one open menu sends, whatever the reader points at. */
export const MAX_INTENT_REQUESTS = 4

/**
 * Locally-editable draft copy of the pieces of a `ViewConfiguration` this menu exposes.
 * Kept flat and keyed on the identifiers the section controls report — `sorts` collapses
 * to a single field because Sort by is single-select in the UI.
 */
type Draft = {
  layout: Layout
  groupBy: string | null
  swimlaneBy: string | null
  sortField: string | null
  visibleFields: ReadonlySet<string>
}

function toDraft(configuration: ViewConfiguration): Draft {
  return {
    layout: configuration.layout,
    groupBy: configuration.groupBy,
    swimlaneBy: configuration.swimlaneBy,
    sortField: configuration.sorts[0]?.field ?? null,
    visibleFields: new Set(configuration.visibleFields),
  }
}

function visibleFieldsChanged(draft: Draft, configuration: ViewConfiguration): boolean {
  return (
    draft.visibleFields.size !== configuration.visibleFields.length ||
    configuration.visibleFields.some(identifier => !draft.visibleFields.has(identifier))
  )
}

/** The settings `draft` changes, in the shape `onApply` takes. */
function draftChanges(draft: Draft, configuration: ViewConfiguration): Partial<ViewConfiguration> {
  const changes: Partial<ViewConfiguration> = {}
  if (draft.layout !== configuration.layout) changes.layout = draft.layout
  if (draft.groupBy !== configuration.groupBy && draft.groupBy !== null) changes.groupBy = draft.groupBy
  if (draft.swimlaneBy !== configuration.swimlaneBy) changes.swimlaneBy = draft.swimlaneBy
  if (draft.sortField !== (configuration.sorts[0]?.field ?? null)) {
    changes.sorts = draft.sortField === null ? [] : [{field: draft.sortField, direction: 'asc'}]
  }
  if (visibleFieldsChanged(draft, configuration)) changes.visibleFields = [...draft.visibleFields]
  return changes
}

const LOAD_ERROR_MESSAGE = 'Unable to load view options'
const noop = () => {}

function LoadError() {
  return (
    <AriaAlert className={styles.loadError}>
      <AlertIcon className={styles.loadErrorIcon} />
      <span className={styles.loadErrorTitle}>{LOAD_ERROR_MESSAGE}</span>
      <span className={styles.loadErrorBody}>Try refreshing the page.</span>
    </AriaAlert>
  )
}

/**
 * Shown when the menu throws while rendering.
 */
function SavedViewOptionsErrorFallback() {
  return (
    <ViewOptionsMenu triggerLabel="View options" title="View options" onApply={noop} onCancel={noop} isDirty={false}>
      <LoadError />
    </ViewOptionsMenu>
  )
}

/** Controlled saved-view options menu shared by domain-specific hosts. */
export function SavedViewOptionsMenu(props: SavedViewOptionsMenuProps) {
  return (
    <ErrorBoundary boundaryName="SavedViewOptionsMenu" fallback={<SavedViewOptionsErrorFallback />}>
      <SavedViewOptionsMenuInner {...props} />
    </ErrorBoundary>
  )
}

function SavedViewOptionsMenuInner({
  fields: view,
  isPending = false,
  isError = false,
  configuration,
  showDirtyIndicator = false,
  configurationDirty,
  disabled = false,
  onApply,
  applyImmediately = false,
  onIntent,
}: SavedViewOptionsMenuProps) {
  // Draft copy of the pieces the surface can edit. Sections read from and write to the
  // draft; nothing propagates to the host until Apply. `onOpenChange` resyncs the draft
  // on open so an external change to `configuration` (e.g. someone else navigated) is not
  // shadowed by a stale draft; every dismissal also resets via `onCancel`. A host that
  // applies immediately has no draft: each edit goes to `onApply` against `configuration`.
  const [draftState, setDraftState] = useState<Draft>(() => toDraft(configuration))
  const configurationDraft = useMemo(() => toDraft(configuration), [configuration])
  const draft = applyImmediately ? configurationDraft : draftState

  const isDirty = Object.keys(draftChanges(draft, configuration)).length > 0

  const groupFields = useMemo(
    () => (view ? getFieldsForCapability(view, Capability.GROUPABLE).map(toFieldOption) : []),
    [view],
  )
  const columnFields = useMemo(
    () => groupFields.map(field => ({...field, disabled: field.identifier === draft.swimlaneBy})),
    [groupFields, draft.swimlaneBy],
  )
  const swimlaneFields = useMemo(
    () => groupFields.map(field => ({...field, disabled: field.identifier === draft.groupBy})),
    [groupFields, draft.groupBy],
  )
  const attributeFields = useMemo(
    () => (view ? getFieldsForCapability(view, Capability.ATTRIBUTABLE).map(toFieldOption) : []),
    [view],
  )
  const sortFields = useMemo(
    () => (view ? getFieldsForCapability(view, Capability.SORTABLE).map(toFieldOption) : []),
    [view],
  )

  const commitChanges = (next: Draft) => {
    const changes = draftChanges(next, configuration)
    if (Object.keys(changes).length > 0) onApply(changes)
  }

  const update = (edit: (current: Draft) => Draft) => {
    if (applyImmediately) commitChanges(edit(draft))
    else setDraftState(edit)
  }

  const intentRequestsRef = useRef(0)
  const intendTo = (edit: (current: Draft) => Draft) => {
    if (!onIntent || intentRequestsRef.current >= MAX_INTENT_REQUESTS) return
    const changes = draftChanges(edit(draft), configuration)
    if (Object.keys(changes).length > 0 && onIntent(changes)) intentRequestsRef.current += 1
  }
  const pickColumn = (current: Draft, next: string): Draft => ({
    ...current,
    groupBy: next,
    // If the picked field is also the current swimlane, clear the swimlane
    // so the two axes never collide, matching the previous direct mapping.
    swimlaneBy: current.swimlaneBy === next ? null : current.swimlaneBy,
  })

  const toggleVisible = (identifier: string, next: boolean) =>
    update(current => {
      const updated = new Set(current.visibleFields)
      if (next) updated.add(identifier)
      else updated.delete(identifier)
      return {...current, visibleFields: updated}
    })

  return (
    <ViewOptionsMenu
      triggerLabel="View options"
      title="View options"
      {...(applyImmediately
        ? {}
        : {
            onApply: () => commitChanges(draft),
            onCancel: () => setDraftState(toDraft(configuration)),
            isDirty,
          })}
      onOpenChange={open => {
        if (!open) return
        intentRequestsRef.current = 0
        if (!applyImmediately) setDraftState(toDraft(configuration))
      }}
      showDirtyIndicator={showDirtyIndicator}
      disabled={disabled}
    >
      <ViewOptionsMenu.Layout
        label="Layout"
        dirtyValue={configurationDirty?.layout ? configuration.layout : undefined}
        trailing={draft.layout === 'board' ? <Label variant="success">Alpha</Label> : undefined}
        options={LAYOUT_OPTIONS}
        value={draft.layout}
        onSelect={next => update(current => ({...current, layout: next}))}
        onIntent={onIntent && (next => intendTo(current => ({...current, layout: next})))}
      />

      {draft.layout === 'board' && (
        <ViewOptionsMenu.Group
          label="Configuration"
          status={
            isPending ? (
              <div className={styles.status}>
                <Spinner srText="Loading view options" />
              </div>
            ) : isError ? (
              <LoadError />
            ) : null
          }
        >
          {view && (
            <>
              <ViewOptionsMenu.MultiSelect
                icon={NoteIcon}
                label="Visible fields"
                showDirtyIndicator={configurationDirty?.visibleFields}
                placeholder="None"
                fields={attributeFields}
                selectedIds={draft.visibleFields}
                onToggle={toggleVisible}
              />
              <ViewOptionsMenu.SingleSelect
                icon={ColumnsIcon}
                label="Column by"
                showDirtyIndicator={configurationDirty?.groupBy}
                placeholder="None"
                fields={columnFields}
                value={draft.groupBy}
                onSelect={next => update(current => pickColumn(current, next))}
                onIntent={onIntent && (next => next !== null && intendTo(current => pickColumn(current, next)))}
              />
              <ViewOptionsMenu.SingleSelect
                icon={RowsIcon}
                label="Swimlane by"
                showDirtyIndicator={configurationDirty?.swimlaneBy}
                placeholder="None"
                fields={swimlaneFields}
                value={draft.swimlaneBy}
                onSelect={next => update(current => ({...current, swimlaneBy: next}))}
                onClear={() => update(current => ({...current, swimlaneBy: null}))}
                onIntent={onIntent && (next => intendTo(current => ({...current, swimlaneBy: next})))}
              />
              <ViewOptionsMenu.SingleSelect
                icon={SortByIcon}
                label="Sort by"
                showDirtyIndicator={configurationDirty?.sorts}
                placeholder="None"
                fields={sortFields}
                value={draft.sortField}
                onSelect={next => update(current => ({...current, sortField: next}))}
                onClear={() => update(current => ({...current, sortField: null}))}
              />
            </>
          )}
        </ViewOptionsMenu.Group>
      )}
    </ViewOptionsMenu>
  )
}
